import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import { useAuth } from "../auth/AuthProvider";
import Workout from "../models/Workout";
import WorkoutSession from "../models/WorkoutSession";
import "react-datepicker/dist/react-datepicker.css";

const LogWorkoutPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialWorkoutId = searchParams.get("workout") || "";

  const [workouts, setWorkouts] = useState([]);
  const [selectedWorkout, setSelectedWorkout] = useState(initialWorkoutId);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [result, setResult] = useState(1);
  const [notes, setNotes] = useState("");
  const [perceivedEffort, setPerceivedEffort] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previousResult, setPreviousResult] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (user) {
      loadWorkouts();
    }
  }, [user]);

  useEffect(() => {
    if (selectedWorkout && user) {
      loadPreviousResult();
    }
  }, [selectedWorkout, user]);

  const loadWorkouts = async () => {
    const workoutList = await Workout.getAll(user.uid);
    setWorkouts(workoutList);
  };

  const loadPreviousResult = async () => {
    const lastSession = await WorkoutSession.getMostRecent(user.uid, selectedWorkout);
    if (lastSession) {
      setPreviousResult(lastSession.result);
      setResult(lastSession.result);
    } else {
      setPreviousResult(null);
    }
  };

  const getCurrentWorkout = () => {
    return workouts.find(w => w.id === selectedWorkout);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedWorkout) {
      alert("Please select a workout!");
      return;
    }

    setLoading(true);
    const formattedDate = selectedDate.toISOString().split("T")[0];
    const workout = getCurrentWorkout();

    try {
      await WorkoutSession.create(user.uid, selectedWorkout, {
        date: formattedDate,
        result,
        resultUnit: workout?.measurementUnit || "rounds",
        notes,
        perceivedEffort,
      });

      setSuccessMessage(`Logged ${result} ${workout?.measurementUnit || "rounds"} for ${workout?.name}`);
      setNotes("");
      setPerceivedEffort(null);

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const workout = getCurrentWorkout();

  return (
    <div className="max-w-md md:max-w-xl lg:max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
      >
        <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 md:p-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Log Session
        </h1>

        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-lg flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Workout Select */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Workout
            </label>
            <select
              value={selectedWorkout}
              onChange={(e) => setSelectedWorkout(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base"
              required
            >
              <option value="">Select a workout</option>
              {workouts.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Picker */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() - 1);
                  setSelectedDate(newDate);
                }}
                className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex-1 text-center">
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  dateFormat="EEE, d MMM yyyy"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-base focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  maxDate={new Date()}
                  popperPlacement="top"
                  wrapperClassName="w-full"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() + 1);
                  if (newDate <= new Date()) {
                    setSelectedDate(newDate);
                  }
                }}
                disabled={selectedDate.toDateString() === new Date().toDateString()}
                className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Result Input */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Result {workout && `(${workout.measurementUnit})`}
              </label>
              <div className="text-right">
                <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {result}
                </span>
                {previousResult !== null && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                    (Previous: {previousResult})
                  </span>
                )}
              </div>
            </div>
            <input
              type="range"
              value={result}
              onChange={(e) => setResult(parseInt(e.target.value, 10))}
              min={1}
              max={Math.max(50, (previousResult || 10) * 2)}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>1</span>
              <span>{Math.max(50, (previousResult || 10) * 2)}</span>
            </div>

            {/* Quick adjust buttons */}
            <div className="flex gap-2 mt-3">
              {[-5, -1, 1, 5].map((delta) => (
                <button
                  key={delta}
                  type="button"
                  onClick={() => setResult(Math.max(1, result + delta))}
                  className="flex-1 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
                >
                  {delta > 0 ? `+${delta}` : delta}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="How did it go?"
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Perceived Effort */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Perceived Effort (RPE) - optional
            </label>
            <div className="flex gap-1 sm:gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setPerceivedEffort(perceivedEffort === num ? null : num)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    perceivedEffort === num
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>Easy</span>
              <span>Max effort</span>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading || !selectedWorkout}
              className="w-1/2 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded font-medium transition-colors"
            >
              {loading ? "Saving..." : "Save Session"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogWorkoutPage;
